import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { PAY_COPY } from "./copy";
import { payAmount, payDate } from "./pay-format";
import type { PayCheckoutView } from "./pay-types";

/**
 * Who is being paid and what for — the two things a payer checks before they
 * look at an address, and the two things a phishing page gets wrong.
 *
 * The legal name is the issuer's **KYB-verified** name read from the checkout
 * response, shown verbatim. There is no logo, no brand string and no
 * client-side mapping: the payer is comparing this against their own records,
 * and anything this page could substitute would defeat the comparison.
 */
export function PayeeAndInvoice({ checkout }: { checkout: PayCheckoutView }) {
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody>
          <CardTitle>{PAY_COPY.payeeHeading}</CardTitle>
          {/* `break-words`: a legal name is arbitrary text and may be long. */}
          <p className="mt-2 break-words text-[18px] font-bold text-ink">{checkout.issuerLegalName}</p>
          <p className="mt-1 text-xs text-muted">{PAY_COPY.payeeVerifiedNote}</p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle>{PAY_COPY.invoiceHeading}</CardTitle>
          <dl className="mt-2 flex flex-col gap-2 text-sm">
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted">{PAY_COPY.invoiceNumberLabel}</dt>
              <dd className="break-all font-semibold text-ink">{checkout.invoiceNumber}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted">{PAY_COPY.invoiceAmountLabel}</dt>
              {/* `payAmount` formats base units with BigInt; the amount is never
                  re-parsed into a float anywhere on this page. */}
              <dd className="font-bold text-ink">{payAmount(checkout.invoiceAmount)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted">{PAY_COPY.invoiceAssetLabel}</dt>
              <dd className="font-semibold text-ink">{checkout.invoiceAmount.asset.symbol}</dd>
            </div>
            {checkout.invoiceDueDate && (
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted">{PAY_COPY.invoiceDueDateLabel}</dt>
                <dd className="font-semibold text-ink">
                  <time dateTime={checkout.invoiceDueDate}>{payDate(checkout.invoiceDueDate)}</time>
                </dd>
              </div>
            )}
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * The anti-phishing line, shown from the moment the invoice loads — before any
 * address exists. That ordering is the point: a payer who is being pushed to a
 * fraudulent address by whoever sent them the link needs this sentence *before*
 * they go looking for an address to send to, not beside one they have already
 * decided to trust.
 */
export function AntiPhishingNotice() {
  return (
    <div role="note" className="rounded-lg border border-warning-border bg-warning-soft px-4 py-3 text-sm text-warning">
      {PAY_COPY.addressAntiPhishing}
    </div>
  );
}
